import AdyenCheckout from '@adyen/adyen-web';
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AdobeTrackingService from '@services/AdobeTrackingService';
import { captureException } from '@services/sentryService';
import { Maybe } from '@types-internal';
import { AdyenConfig } from '@types-internal/AdyenTypes';
import { handleActionCreatorFailureRedirect, handleQrCodeFailureRedirect } from '@utils/redirect';
import { getMobileAppType, isRecognizedApp } from '@utils/retryUtils';

interface ActionCreatorConfig extends AdyenConfig {
  locale: string;
  paymentId?: string;
  /**
   * When provided, this is invoked once retries are exhausted instead of the
   * legacy shop redirects. Used by the extracted authorization-action page so
   * the final navigation goes through processPaymentDetailsUrl rather than the
   * legacy Finish/app-payment-router routes.
   */
  onRetryExhausted?: () => void;
}
export type BancontactMobileDetails = { payload: string };
export type ThreeDSDetails = { threeDSResult: string };
export type Details = BancontactMobileDetails | ThreeDSDetails;
export type PaymentDetailsState = { data?: { details?: Details } };
const translations = (locale: string) => {
  return {
    [locale]: {},
  };
};

export const ACTION_CREATOR_CONTAINER = 'action_creator_container';

const MAX_RETRIES = 2;
const RETRY_WAIT = 2000;
const ACTION_CREATOR_TIMEOUT = 1000 * 60 * 15;

const incrementRetryCount = (count: number) => count + 1;

type MountAdyenActionParams = {
  adyenEnvironment: AdyenConfig['adyenEnvironment'];
  clientKey: AdyenConfig['clientKey'];
  locale: string;
  paymentAction: PaymentAction;
  onError: (error: unknown) => void;
  onAdditionalDetails: (state: PaymentDetailsState) => void;
  onActionTimeout: () => void;
};

/**
 * Creates the Adyen checkout instance and mounts the payment action (3DS challenge / QR code).
 * Returns the mounted UI element and the timeout id so the caller can clean them up.
 */
const mountAdyenAction = async ({
  adyenEnvironment,
  clientKey,
  locale,
  paymentAction,
  onError,
  onAdditionalDetails,
  onActionTimeout,
}: MountAdyenActionParams): Promise<{ action?: UIElement; actionTimeOutId?: NodeJS.Timeout }> => {
  const adyen = await AdyenCheckout({
    environment: adyenEnvironment,
    translations: translations(locale),
    locale,
    clientKey,
    onError,
    onAdditionalDetails,
  });
  if (!adyen) {
    throw new Error('could not create Adyen action object authorization component');
  }
  const threeDSConfiguration = {
    challengeWindowSize: '05', // ['100%', '100%']
  };
  const action = adyen
    .createFromAction(paymentAction, threeDSConfiguration)
    .mount(`#${ACTION_CREATOR_CONTAINER}`);

  // Send tracking event if this 3ds challenge page is displayed
  if (paymentAction.type === 'threeDS2') {
    AdobeTrackingService.adyenTracking('3DSdisplay');
  }

  const actionTimeOutId = setTimeout(onActionTimeout, ACTION_CREATOR_TIMEOUT);
  return { action, actionTimeOutId };
};

const useActionCreator = (
  paymentAction: PaymentAction,
  config: ActionCreatorConfig,
  isMocked = false,
) => {
  const { adyenEnvironment, locale, paymentId, clientKey, onRetryExhausted } = config;

  // Kept in a ref so changing the handler identity does not re-initialize Adyen.
  const onRetryExhaustedRef = useRef(onRetryExhausted);
  onRetryExhaustedRef.current = onRetryExhausted;

  const [details, setDetails] = useState<Maybe<Details>>();
  const [, setActionTimeoutId] = useState<NodeJS.Timeout>();
  const [retryCount, setRetryCount] = useState(0);
  const [isActionTimedOut, setActionTimedOut] = useState<boolean>(false);
  const [mobileAppType] = useState(() => getMobileAppType());
  const isMobileApp = isRecognizedApp(mobileAppType) ?? false;

  const onAdditionalDetails = useCallback((state: PaymentDetailsState) => {
    setActionTimeoutId(timerId => {
      clearTimeout(timerId);
      return undefined;
    });
    if (!isActionTimedOut) {
      setDetails(state?.data?.details);
    }
  }, []);

  const paymentActionMemo = useMemo(
    () => paymentAction,
    [
      paymentAction?.type,
      paymentAction?.paymentMethodType,
      paymentAction?.url,
      paymentAction?.paymentData,
      paymentAction?.authorisationToken,
    ],
  );

  useEffect(() => {
    let action: Maybe<UIElement>;
    let retryTimeoutId: Maybe<NodeJS.Timeout>, actionTimeOutId: Maybe<NodeJS.Timeout>;
    const scheduleRetry = () => setRetryCount(incrementRetryCount);
    const onError = (error: any) => {
      if (error?.errorCode === 'timeout') {
        setActionTimedOut(true);
        clearTimeout(actionTimeOutId);
        return;
      }
      AdobeTrackingService.adyenTracking('3DSsubmitKO');
      captureException({
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          errorCustomMessage: '3DS error captured',
          error,
          paymentId,
        },
      });
      retryTimeoutId = setTimeout(scheduleRetry, RETRY_WAIT);
    };

    async function initialize() {
      try {
        const mounted = await mountAdyenAction({
          adyenEnvironment,
          clientKey,
          locale,
          onError,
          onAdditionalDetails,
          paymentAction: paymentActionMemo,
          onActionTimeout: () => setActionTimedOut(true),
        });
        action = mounted.action;
        actionTimeOutId = mounted.actionTimeOutId;
        setActionTimeoutId(actionTimeOutId);
      } catch (error) {
        onError(error);
      }
    }

    const handleRetriesExhausted = () => {
      if (onRetryExhaustedRef.current) {
        onRetryExhaustedRef.current();
      } else if (isMocked) {
        // retries exhausted in a test context, nothing to do
      } else if (isMobileApp) {
        handleActionCreatorFailureRedirect();
      } else {
        handleQrCodeFailureRedirect();
      }
    };

    if (retryCount < MAX_RETRIES) {
      initialize();
    } else {
      handleRetriesExhausted();
    }

    return () => {
      action?.unmount().remove();
      clearTimeout(retryTimeoutId);
      clearTimeout(actionTimeOutId);
    };
  }, [adyenEnvironment, locale, clientKey, paymentActionMemo, onAdditionalDetails, retryCount]);

  return { details, isActionTimedOut };
};

export default useActionCreator;
